import { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Modal from '../modal/modal';
import Card from '../card/Card';
import P from '../texts/p/P';
import Button from '../button/Button';
import Input from '../input/Input';
import Icon from '../icon/Icon';
import Spinner from '../Spinner/spinner';
import Span from '../texts/span/Span';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { ReferrerName } from '../../types/interfaces';
import { getReferrerNames } from '../../services/ReferrerNameService';

interface ReferrersManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReferrerUpdated?: () => void;
}

interface ReferrerFormData {
  name: string;
  code: string;
}

// Estilos para el contenedor con scroll
const ScrollableContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  
  /* Estilos para el scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

// Estilos para las filas de la tabla
const TableRow = styled.div<{ isEditing?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s;
  background-color: ${({ isEditing }) => (isEditing ? '#f5f5f5' : 'transparent')};
  
  &:hover {
    background-color: ${({ isEditing }) => (isEditing ? '#f5f5f5' : '#fafafa')};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-bottom: 2px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ReferrerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0; /* Permite que el texto se trunque */
`;

const ReferrerNameText = styled(P)`
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReferrerCodeText = styled(P)`
  font-size: 13px;
  color: #666;
  margin: 0;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  border: 1px solid ${theme.colors.primary.border.regular};
  border-radius: ${theme.borderRadius['border-radius-3']};
  background-color: ${theme.colors.neutral.bg.inverted};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover:not(:disabled) {
    background-color: ${theme.colors.primary.bg.weak};
    border-color: ${theme.colors.primary.border.strong};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: ${theme.colors.neutral.border.weak};
  }
  
  /* Estilo especial para botón de eliminar */
  &.delete-button:hover:not(:disabled) {
    background-color: ${theme.colors.danger.bg.weak || '#FFE6E6'};
    border-color: ${theme.colors.danger.border.regular};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #999;
`;

const FormContainer = styled(Card)`
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
`;

const ListContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;

const ReferrersManagementModal: React.FC<ReferrersManagementModalProps> = ({
  isOpen,
  onClose,
  onReferrerUpdated,
}) => {
  const {
    referrerNames,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    createReferrer,
    updateReferrer,
    removeReferrer,
    refreshReferrerNames,
  } = useReferrerNames();

  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ReferrerFormData>({ name: '', code: '' });
  const [formErrors, setFormErrors] = useState<{ name?: string; code?: string }>({});
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Función para generar el siguiente código incremental
  const generateNextCode = (referrersList?: ReferrerName[], startFrom?: number): string => {
    const listToUse = referrersList || referrerNames;
    
    if (listToUse.length === 0) {
      return '001';
    }

    // Obtener todos los códigos numéricos existentes
    const existingCodes = new Set(
      listToUse
        .map((r) => {
          const num = parseInt(r.code, 10);
          return isNaN(num) ? 0 : num;
        })
        .filter((num) => num > 0)
    );

    // Si no hay códigos numéricos, empezar desde 001
    if (existingCodes.size === 0) {
      return '001';
    }

    // Encontrar el siguiente código disponible
    // Si se proporciona startFrom, empezar desde ahí, sino desde el máximo + 1
    const maxCode = Math.max(...Array.from(existingCodes));
    let nextCode = startFrom || (maxCode + 1);
    
    // Buscar el siguiente código disponible (puede haber huecos por códigos deshabilitados)
    // Incrementar hasta encontrar uno que no esté en la lista
    // Nota: Los códigos deshabilitados no aparecen en la lista, pero existen en la BD
    // Por eso el backend puede rechazarlos. El loop de reintentos manejará esto.
    while (existingCodes.has(nextCode) && nextCode < 999) {
      nextCode++;
    }

    // Formatear con 3 dígitos
    return nextCode.toString().padStart(3, '0');
  };

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setShowNewForm(false);
      setEditingId(null);
      setDeletingId(null);
      setFormData({ name: '', code: '' });
      setFormErrors({});
      setSearchTerm('');
    }
  }, [isOpen]);

  // Filtrar referidos basado en el término de búsqueda
  const filteredReferrers = referrerNames.filter((referrer) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return referrer.name.toLowerCase().includes(searchLower);
  });

  // Cargar datos del referido cuando se edita
  useEffect(() => {
    if (editingId !== null) {
      const referrer = referrerNames.find((r) => r.id === editingId);
      if (referrer) {
        setFormData({ name: referrer.name, code: referrer.code });
        setFormErrors({});
      }
    }
  }, [editingId, referrerNames]);

  const validateForm = (): boolean => {
    const errors: { name?: string; code?: string } = {};
    if (!formData.name.trim()) {
      errors.name = 'El nombre es obligatorio';
    }
    // Solo validar código si se está editando (al crear se autogenera)
    if (editingId !== null && !formData.code.trim()) {
      errors.code = 'El código es obligatorio';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNewReferrer = () => {
    const nextCode = generateNextCode();
    setShowNewForm(true);
    setEditingId(null);
    setFormData({ name: '', code: nextCode });
    setFormErrors({});
  };

  const handleEditReferrer = (referrer: ReferrerName) => {
    setEditingId(referrer.id);
    setShowNewForm(false);
    setFormData({ name: referrer.name, code: referrer.code });
    setFormErrors({});
  };

  const handleCancelForm = () => {
    setShowNewForm(false);
    setEditingId(null);
    setFormData({ name: '', code: '' });
    setFormErrors({});
  };

  const handleSaveReferrer = async () => {
    if (!validateForm()) {
      return;
    }

    // Asegurar que el código esté generado al crear
    let codeToSave = showNewForm && !editingId 
      ? (formData.code || generateNextCode())
      : formData.code.trim();

    // Si es creación, intentar con reintentos en caso de código duplicado
    if (showNewForm && !editingId) {
      const maxRetries = 5;
      let retries = 0;
      let result = await createReferrer(formData.name.trim(), codeToSave);

      // Si hay error de código duplicado (409 o DUPLICATE_CODE), reintentar con nuevo código
      while (!result.success && (result.status === 409 || result.code === 'DUPLICATE_CODE') && retries < maxRetries) {
        retries++;
        console.log(`[ReferrersManagementModal] Código duplicado detectado, reintentando con nuevo código (intento ${retries}/${maxRetries})`);
        console.log(`[ReferrersManagementModal] Código que falló: ${codeToSave}`);
        
        // Recargar la lista de referidos para obtener el código más actualizado
        await refreshReferrerNames();
        
        // Obtener los referidos actualizados directamente del servicio para evitar problemas de estado
        // (el estado de React puede no haberse actualizado aún)
        const updatedListResponse = await getReferrerNames();
        
        // Generar nuevo código basado en la lista actualizada
        // Si el código anterior falló, empezar desde ese código + 1
        const previousCodeNum = parseInt(codeToSave, 10);
        const startFrom = isNaN(previousCodeNum) ? undefined : previousCodeNum + 1;
        
        if (updatedListResponse.success && updatedListResponse.data) {
          codeToSave = generateNextCode(updatedListResponse.data, startFrom);
          console.log(`[ReferrersManagementModal] Lista actualizada tiene ${updatedListResponse.data.length} referidos`);
          console.log(`[ReferrersManagementModal] Códigos existentes:`, updatedListResponse.data.map(r => r.code).slice(-10));
        } else {
          // Fallback: usar el estado si el servicio falla
          codeToSave = generateNextCode(undefined, startFrom);
        }
        
        setFormData({ ...formData, code: codeToSave });
        console.log(`[ReferrersManagementModal] Nuevo código generado: ${codeToSave}`);
        
        // Reintentar con el nuevo código
        result = await createReferrer(formData.name.trim(), codeToSave);
      }

      if (result.success) {
        handleCancelForm();
        if (onReferrerUpdated) {
          onReferrerUpdated();
        }
      } else {
        // El error ya está manejado en el hook, pero podemos mostrar un mensaje adicional si es necesario
        console.error('Error saving referrer:', result.message);
      }
    } else {
      // Para edición, comportamiento normal
      const result = await updateReferrer(editingId!, formData.name.trim(), codeToSave);
      
      if (result.success) {
        handleCancelForm();
        if (onReferrerUpdated) {
          onReferrerUpdated();
        }
      } else {
        console.error('Error saving referrer:', result.message);
      }
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeletingId(id);
  };

  const handleConfirmDelete = async () => {
    if (deletingId === null) return;

    const result = await removeReferrer(deletingId);
    if (result.success) {
      setDeletingId(null);
      if (onReferrerUpdated) {
        onReferrerUpdated();
      }
    } else {
      console.error('Error deleting referrer:', result.message);
    }
  };

  const handleCancelDelete = () => {
    setDeletingId(null);
  };

  const handleClose = () => {
    if (!isCreating && !isUpdating && !isDeleting) {
      handleCancelForm();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title='Gestionar referidos'
      primaryVariant='primary'
      primaryText='Cerrar'
      onPrimaryAction={handleClose}
      primaryActionDisabled={isCreating || isUpdating || isDeleting}
      width='700px'
      overflow='hidden'
    >
      <Card display='grid' gap='16px' style={{ maxHeight: '70vh', overflow: 'hidden' }}>
        {isLoading ? (
          <Card display='flex' justifyContent='center' margin={{ top: '4', bottom: '4' }}>
            <Spinner size='md' variant='primary' />
          </Card>
        ) : (
          <>
            {/* Header con botón de nuevo */}
            <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '2' }}>
              <Card display='flex' flexDirection='column' gap='4px'>
                <P type='1' variant='neutral-1' weight='bold' withMargin={false}>
                  Lista de referidos
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  {filteredReferrers.length} de {referrerNames.length} {referrerNames.length === 1 ? 'referido' : 'referidos'}
                </P>
              </Card>
              {!showNewForm && editingId === null && (
                <Button variant='primary' size='md' onClick={handleNewReferrer}>
                  <Icon name='add_outline' variant='neutral' modifier='inverted' width={18} height={18} />
                  Nuevo referido
                </Button>
              )}
            </Card>

            {/* Buscador */}
            {!showNewForm && editingId === null && referrerNames.length > 0 && (
              <Card margin={{ bottom: '2' }}>
                <Input
                  label=''
                  id='search-referrers'
                  placeholder='Buscar por nombre...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icons={{ iconLeft: 'search_outline' }}
                  autocomplete='off'
                />
              </Card>
            )}

            {/* Formulario de nuevo/editar */}
            {(showNewForm || editingId !== null) && (
              <FormContainer padding='4'>
                <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
                  <P type='2' variant='neutral-1' weight='bold' withMargin={false}>
                    {editingId ? 'Editar referido' : 'Nuevo referido'}
                  </P>
                  <Button
                    variant='tertiary'
                    size='sm'
                    onClick={handleCancelForm}
                    disabled={isCreating || isUpdating}
                  >
                    <Icon name='cancel_outline' variant='neutral' height={18} />
                  </Button>
                </Card>
                <Card display='grid' gap='16px'>
                  <Card display='grid' gap='8px'>
                    <Input
                      label='Nombre *'
                      id='referrer-name-input'
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (formErrors.name) {
                          setFormErrors({ ...formErrors, name: undefined });
                        }
                      }}
                      inputStatus={formErrors.name ? 'error' : 'normal'}
                      maxLength={300}
                      placeholder='Ingrese el nombre del referido'
                    />
                    {formErrors.name && (
                      <Span variant='danger' type='2'>
                        {formErrors.name}
                      </Span>
                    )}
                  </Card>
                  {editingId !== null && (
                    <Card display='grid' gap='8px'>
                      <Input
                        label='Código *'
                        id='referrer-code-input'
                        value={formData.code}
                        onChange={(e) => {
                          setFormData({ ...formData, code: e.target.value });
                          if (formErrors.code) {
                            setFormErrors({ ...formErrors, code: undefined });
                          }
                        }}
                        inputStatus={formErrors.code ? 'error' : 'normal'}
                        maxLength={50}
                        placeholder='Ingrese el código del referido'
                      />
                      {formErrors.code && (
                        <Span variant='danger' type='2'>
                          {formErrors.code}
                        </Span>
                      )}
                    </Card>
                  )}
                  <Card display='flex' gap='12px' justifyContent='flex-end' margin={{ top: '2' }}>
                    <Button
                      variant='secondary'
                      size='md'
                      onClick={handleCancelForm}
                      disabled={isCreating || isUpdating}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant='primary'
                      size='md'
                      onClick={handleSaveReferrer}
                      disabled={isCreating || isUpdating}
                    >
                      {isCreating || isUpdating ? (
                        <Spinner size='sm' variant='primary' />
                      ) : editingId ? (
                        'Guardar cambios'
                      ) : (
                        'Crear referido'
                      )}
                    </Button>
                  </Card>
                </Card>
              </FormContainer>
            )}

            {/* Lista de referidos con scroll */}
            {referrerNames.length === 0 && !showNewForm && editingId === null ? (
              <EmptyState>
                <Icon name='info_circle_outline' variant='neutral' modifier='regular' height={48} />
                <P type='2' variant='neutral' withMargin={false} margin={{ top: '2' }}>
                  No hay referidos registrados
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  Haz clic en "Nuevo referido" para agregar uno
                </P>
              </EmptyState>
            ) : filteredReferrers.length === 0 && searchTerm.trim() ? (
              <EmptyState>
                <Icon name='search_outline' variant='neutral' modifier='regular' height={48} />
                <P type='2' variant='neutral' withMargin={false} margin={{ top: '2' }}>
                  No se encontraron referidos
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  Intenta con otro término de búsqueda
                </P>
              </EmptyState>
            ) : (
              <ListContainer>
                <TableHeader>
                  <P type='2' variant='neutral-1' weight='bold' withMargin={false}>
                    Referido
                  </P>
                  <P type='2' variant='neutral-1' weight='bold' withMargin={false}>
                    Acciones
                  </P>
                </TableHeader>
                <ScrollableContainer>
                  {filteredReferrers.map((referrer) => (
                    <TableRow key={referrer.id} isEditing={editingId === referrer.id}>
                      {editingId === referrer.id ? (
                        <Card padding='2'>
                          <P type='3' variant='neutral' withMargin={false}>
                            Editando...
                          </P>
                        </Card>
                      ) : (
                        <>
                          <ReferrerInfo>
                            <ReferrerNameText type='2' variant='neutral-1' withMargin={false}>
                              {referrer.name}
                            </ReferrerNameText>
                          </ReferrerInfo>
                          <ActionsContainer>
                            {deletingId === referrer.id ? (
                              <Card display='flex' gap='8px' alignItems='center'>
                                <P type='3' variant='neutral' withMargin={false}>
                                  ¿Eliminar?
                                </P>
                                <Button
                                  variant='secondary'
                                  size='sm'
                                  onClick={handleConfirmDelete}
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <Spinner size='sm' variant='primary' />
                                  ) : (
                                    'Sí'
                                  )}
                                </Button>
                                <Button
                                  variant='tertiary'
                                  size='sm'
                                  onClick={handleCancelDelete}
                                  disabled={isDeleting}
                                >
                                  No
                                </Button>
                              </Card>
                            ) : (
                              <>
                                <ActionButton
                                  onClick={() => handleEditReferrer(referrer)}
                                  disabled={isDeleting || deletingId !== null}
                                  title='Editar referido'
                                  type='button'
                                >
                                  <Icon name='edit_outline' variant='neutral' modifier='strong' width={20} height={20} />
                                </ActionButton>
                                <ActionButton
                                  className='delete-button'
                                  onClick={() => handleDeleteClick(referrer.id)}
                                  disabled={isDeleting || editingId !== null}
                                  title='Eliminar referido'
                                  type='button'
                                >
                                  <Icon name='delete_outline' variant='danger' modifier='regular' width={20} height={20} />
                                </ActionButton>
                              </>
                            )}
                          </ActionsContainer>
                        </>
                      )}
                    </TableRow>
                  ))}
                </ScrollableContainer>
              </ListContainer>
            )}
          </>
        )}
      </Card>
    </Modal>
  );
};

export default ReferrersManagementModal;
